/**
 * CanonChart — canon v3+v4 chart primitives (WAVE Visual-v4 P3.1).
 *
 * Centralises the spec we've been re-implementing inline across PMC,
 * AnalyticsPage, ResiliencePage, SwolfTrendCard, TimelineChart. Wraps
 * recharts primitives with canon defaults so individual charts stop
 * carrying hardcoded #3b82f6/#ef4444/#22c55e and instead compose from
 * canonical colors + faint grid + iris now-marker + БАЗА X reference.
 *
 * Canon refs:
 *   v3 раздел 8 — Данные на графиках (readability > decoration)
 *   v3 section 6 — 7 Coggan zones
 *   v3 section 7 — Discipline identity (swim cyan / bike coral / run lime)
 *   v4 раздел графики — faint grid · БАЗА X · corsair iris band · area-fill ≤10%
 */
import type { ReactNode } from "react";

import { axisLabel } from "../../../../chart-core/chartAxisLabel";

// ─── Canon palette ───────────────────────────────────────────────────

/** Main data line — ink-1 white per canon v3 section 8. */
export const CANON_LINE_PRIMARY = "var(--chart-line-1)";

/** Secondary line (ATL, fatigue trace) — cold steel, не синий
 *  (синий конфликтует с swim). Canon v3 раздел 8. */
export const CANON_LINE_SECONDARY = "var(--chart-line-2)";

/** Tertiary line (HRV, TSB context) — light steel. Canon v3 section 8 forbids
 *  green here (would be evaluation, not data). */
export const CANON_LINE_TERTIARY = "var(--chart-line-3)";

/** Iris main brand colour for now-marker, focus state, baseline emphasis. */
export const CANON_IRIS = "var(--brand-indigo-500)";
export const CANON_IRIS_LIGHT = "var(--brand-indigo-300)";

/** Faint canon grid — almost invisible, lets data carry the eye. */
export const CANON_GRID_STROKE = "rgba(230, 232, 255, 0.04)";

/** Dashed base reference line — surfaces baselines like «БАЗА FTP». */
export const CANON_BASELINE_STROKE = "rgba(230, 232, 255, 0.18)";

/** Tooltip background — canon bg-3 inset card. */
/* Фон тултипа = подложка «якорной» карточки. Хардкод-hex был её копией
   и не следовал бы теме. */
export const CANON_TOOLTIP_BG = "var(--indigo-anchor-bg)";

/**
 * Палитра дисциплин для графиков — ССЫЛКАМИ на канон, не копией.
 *
 * Копия здесь стояла hex'ами и успела разойтись: strength был #8B8E88, то
 * есть серым цветом «нет данных» (--fg-muted), тогда как канон 24.07 дал
 * силовой фиолет #8A63C9. Три остальных совпадали случайно — их просто ещё
 * не успели поменять.
 */
export const SPORT_COLOR_CANON = {
  swim: "var(--brand-disc-swim)",
  bike: "var(--brand-disc-bike)",
  run: "var(--brand-disc-run)",
  strength: "var(--brand-disc-strength)",
} as const;
export type SportKey = keyof typeof SPORT_COLOR_CANON;

/**
 * Семь зон Coggan — ССЫЛКАМИ на канон (design-tokens/brand.css).
 *
 * Здесь лежала вторая копия шкалы, набранная hex'ами, и две ступени из семи
 * от канона отстали: Z1 был #5A6EE0 против #8796E9, Z3 — #79CF3D против
 * #67B92E. Это ровно те ступени, чью РАЗЛИЧИМОСТЬ калибровали отдельно
 * (гарантированный шаг светлоты между соседями — см. обоснование у
 * --brand-zone-* в brand.css), то есть копия молча ломала то, ради чего
 * калибровка делалась.
 *
 * Второй выигрыш от ссылок: у зон в brand.css есть СВОЯ светлая лестница
 * (на светлом фоне шкала целиком сдвинута вниз по светлоте, иначе верхние
 * ступени не набирают контраста). Плоский hex за темой не шёл — графики зон
 * в светлой теме показывали тёмные значения. Теперь идёт.
 */
export const ZONE_PALETTE_7 = [
  "var(--brand-zone-1)", // Z1 recovery / active
  "var(--brand-zone-2)", // Z2 endurance
  "var(--brand-zone-3)", // Z3 tempo
  "var(--brand-zone-4)", // Z4 threshold
  "var(--brand-zone-5)", // Z5 VO2max
  "var(--brand-zone-6)", // Z6 anaerobic
  "var(--brand-zone-7)", // Z7 neuromuscular
] as const;

/**
 * Три ступени «плохо / внимание / хорошо» для графиков.
 *
 * Имя историческое: таблица называлась шкалой кольца готовности, но само
 * кольцо (ReadinessRing) её никогда не использовало — оно с самого начала
 * читает семантические токены. Единственный потребитель — PMCChart.
 *
 * Значения стояли hex'ами и отстали от канон-сессии 24.07: `good` держал
 * лайм #7BC93F, выведенный из роли «хорошо» (лайм остаётся цветом disc-run,
 * иначе один тон значит и «бег», и «хорошо»). В ReadinessRing эту правку
 * сделали, здесь — нет: то же решение, применённое к одной копии из двух.
 *
 * Ступень `peak` (#19E88A, «85+») удалена — её не запрашивал никто.
 */
export const RING_BANDS = {
  bad: "var(--bad)",    // < 50
  warn: "var(--warn)",  // 50–69
  good: "var(--ok)",    // 70+
} as const;

// ─── Наборы пропсов для детей графика ────────────────────────────────
//
// ⚠️ ЗДЕСЬ НЕ МОЖЕТ БЫТЬ КОМПОНЕНТОВ-ОБЁРТОК. Recharts разбирает детей
// графика по displayName: `renderByOrder` берёт `getDisplayName(child.type)`,
// ищет обработчик в своей карте и — если обработчика нет — **молча
// выбрасывает ребёнка**, ничего не рендеря и ничего не сообщая.
//
// Так и вышло: `<CanonGrid />` (обёртка над CartesianGrid) стоял в PMCChart
// и SwolfTrendCard, и у кривой формы на экране не было НИ ОДНОЙ линии сетки,
// пока у соседней карточки с прямым `<CartesianGrid>` их было десять. Тесты
// этого не видели: моки recharts рисуют заглушки и не воспроизводят разбор
// детей. Проверить можно только глазами или по DOM живой страницы.
//
// Поэтому общее знание живёт здесь НАБОРАМИ ПРОПСОВ, а вызывающий ставит
// настоящий примитив recharts прямым ребёнком:
//     <CartesianGrid {...canonGridProps} />
// Guard на это правило — CanonChart.test.ts.

/**
 * Бледная канон-сетка (v4 раздел графики): только горизонтали, почти невидима —
 * взгляд ведут данные, а не разметка.
 */
export const canonGridProps = {
  stroke: CANON_GRID_STROKE,
  strokeDasharray: "2 4",
  horizontal: true,
  vertical: false,
} as const;

/**
 * Пропсы маркера «сейчас» — вертикаль ирисом. Канон v4 раздел графики.
 * Ставится как `<ReferenceLine x={день} {...canonNowMarkerProps(подпись)} />`.
 */
export function canonNowMarkerProps(label?: string) {
  return {
    stroke: CANON_IRIS,
    strokeWidth: 1.4,
    label: label
      ? {
          value: label,
          fill: CANON_IRIS_LIGHT,
          fontSize: 9.5,
          fontFamily: "JetBrains Mono, ui-monospace, monospace",
          position: "top" as const,
        }
      : undefined,
  };
}

/**
 * Пропсы базовой пунктирной линии («БАЗА FTP 280»). Канон v4 раздел графики.
 * Ставится как `<ReferenceLine y={280} {...canonBaselineProps("БАЗА FTP 280")} />`.
 */
export function canonBaselineProps(label: string) {
  return {
    stroke: CANON_BASELINE_STROKE,
    strokeWidth: 1,
    strokeDasharray: "4 4",
    label: {
      value: label,
      fill: "var(--fg-muted)",
      fontSize: 9.5,
      fontFamily: "JetBrains Mono, ui-monospace, monospace",
      letterSpacing: "0.18em",
      position: "insideTopLeft" as const,
    },
  };
}

/**
 * Подпись единицы измерения к оси Y.
 *
 * ЗАЧЕМ. Голая шкала «30…150» — не число, а загадка: она одинаково похожа на
 * ватты, на TSS и на проценты. Подпись стоит ВДОЛЬ оси, а не в заголовке
 * карточки, потому что у двухосевых графиков заголовок один, а шкалы две.
 *
 * Если у оси есть `unit=` (проценты, градусы), подпись не нужна — единица уже
 * стоит у каждого деления.
 *
 * Форма подписи живёт в `chart-core`, а не здесь: те же четыре карточки
 * («Объём», «Самочувствие», «Форма», «Нагрузка») есть и в тренерской админке
 * отдельной копией, и подписи у двух ролей обязаны совпадать.
 */
export const canonAxisLabel = axisLabel;

/**
 * Пропсы легенды. Мелкая, без рамки — она подпись, а не второй заголовок.
 * Ставится как `<Legend {...canonLegendProps} formatter={legendLabels(map)} />`.
 *
 * КОГДА НУЖНА. Только когда рядов больше одного И различает их ТОЛЬКО цвет.
 * Если у карточки под графиком уже стоит цветной список (травмы в PainLoad,
 * фишки линз в LensOverlay) — легенда будет вторым словарём о том же, и её
 * не добавляют.
 */
export const canonLegendProps = {
  wrapperStyle: { fontSize: 11, color: "var(--fg-secondary)" },
  iconSize: 8,
} as const;

/**
 * Переводчик имён рядов для легенды: `dataKey` латиницей → человеческое имя.
 * Незнакомый ключ возвращается как есть — легенда не должна пропасть из-за
 * одного неописанного ряда.
 *
 * ═══ ТЕКСТ НЕЙТРАЛЬНЫЙ, ЦВЕТ — У ФИШКИ (2026-08-04) ═══
 *
 * Recharts красит подпись ряда цветом самого ряда, перебивая `wrapperStyle`
 * инлайном. Для светлых линий это давало нечитаемый текст: замер на живом
 * экране — «Свежесть (TSB)» 3.32 : 1 при норме 4.5, «Усталость (ATL)» 4.33.
 *
 * Ряд различает ФИШКА слева — она и остаётся цветной. Дублировать цвет ещё
 * и в тексте незачем, а расплачивается за это читаемость. Тот же приём, что
 * с `--bad-text` против `--bad`: у показа и у подписи разные требования к
 * контрасту, и роль решается отдельно от значения.
 */
export function legendLabels(map: Record<string, string>) {
  return (value: string) => (
    <span style={{ color: "var(--fg-secondary)" }}>{map[value] ?? value}</span>
  );
}

/**
 * CanonAxisTick — moves Y-tick formatting to canon mono ink-3.
 * Pass as `<YAxis tick={canonAxisTick}>`.
 */
/**
 * Размер подписи оси — 11px, а не 10 (2026-08-04).
 *
 * Замер экрана Анализа: 178 из 252 текстовых узлов были мельче 11px, и
 * 148 из них — именно тики осей. Плотность экрана шла не от количества
 * данных, а от мелкого кегля, и читалось это как «тесно», а не «богато».
 *
 * Одиннадцать, а не двенадцать: подписи оси Y отъедают ширину у самого
 * графика, и на телефоне каждый лишний пиксель кегля — минус площадь
 * кривой. Recharts прорежает подписи сам (`minTickGap`), поэтому налезать
 * друг на друга они не начнут.
 */
export const canonAxisTick = {
  fill: "var(--fg-muted)",
  fontSize: 11,
  fontFamily: "JetBrains Mono, ui-monospace, monospace",
};

/**
 * Tooltip style object compatible with `<Tooltip contentStyle={…}>`.
 * Solid bg-3 (inset card) with hairline border, mono date label.
 */
export const canonTooltipContentStyle = {
  backgroundColor: CANON_TOOLTIP_BG,
  border: "1px solid rgba(230, 232, 255, 0.08)",
  borderRadius: 11,
  fontSize: 12,
  color: "var(--fg-primary)",
  boxShadow: "var(--shadow-tooltip)",
};
export const canonTooltipLabelStyle = {
  color: "var(--fg-primary)",
  fontWeight: 600,
  fontSize: 11,
};
export const canonTooltipItemStyle = {
  // Был #BDC4E4 — холодный сиреневый ink из iris-эпохи; канон-сессия увела
  // вторичный текст в тёплый крем. Литерал остался единственным местом,
  // где старый оттенок пережил переход.
  color: "var(--fg-secondary)",
  fontVariantNumeric: "tabular-nums" as const,
};

// ─── Composable card wrapper ─────────────────────────────────────────

/**
 * CanonChartCard — indigo-card surrounding a chart + optional section
 * tick + mono eyebrow header. Pin canon depth + spacing in one place.
 */
export function CanonChartCard({
  title,
  eyebrow,
  rightSlot,
  children,
}: {
  title?: string;
  eyebrow?: string;
  rightSlot?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="indigo-card p-4 space-y-2">
      {(title || eyebrow) && (
        /*
          НА УЗКОМ ЭКРАНЕ КОНТРОЛЫ УХОДЯТ ПОД ЗАГОЛОВОК — 2026-08-04.

          Заголовок и кнопки делили одну строку на любой ширине. Кнопки не
          сжимаются, поэтому на 390px заголовку оставалось ~130px, и
          «Форма / Усталость / Свежесть» вставало столбиком по слову на
          строку — вместе с надзаголовком. Перенос строки для набора кнопок
          дешевле, чем разорванное название карточки.
        */
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="min-w-0">
            {eyebrow && (
              <div className="indigo-section-eyebrow">{eyebrow}</div>
            )}
            {title && (
              <div className="text-sm font-semibold text-fg-primary leading-tight">
                {title}
              </div>
            )}
          </div>
          {rightSlot && <div className="shrink-0">{rightSlot}</div>}
        </div>
      )}
      {children}
    </div>
  );
}
